import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp } from "./RevealText";
import { FALLBACK_TICKET_URL } from "../../lib/api";
import { useLang, loc, T } from "../../lib/i18n";

const Card = ({ ev, i, lang, view }) => (
  <motion.div
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    className={i === 1 ? "md:translate-y-16" : ""}
  >
    <Link to={`/events/${ev.slug}`} data-testid={`event-card-${ev.slug}`} className="group block">
      <div className="relative overflow-hidden rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <img
          src={ev.poster}
          alt={ev.title}
          loading="lazy"
          className="w-full aspect-[3/4] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-5 left-5 rounded-full bg-white/85 backdrop-blur-md px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-slate-900">
          {loc(ev, "date", lang)}
        </div>
        <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
          <h3 className="font-serif text-white text-3xl md:text-4xl tracking-tight">{ev.title}</h3>
          <p className="text-white/70 text-sm mt-2 line-clamp-2">{loc(ev, "tagline", lang)}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.2em]">
            {view}
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const EventCards = ({ events, ticketUrl }) => {
  const { lang } = useLang();
  const t = T[lang].events;
  return (
    <section data-testid="events-section" className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-slate-900 leading-[1.05]">
            {t.title1}<br />
            <span className="italic text-[#00B4D8]">{t.title2}</span>
          </h2>
        </div>
        <a
          href={ticketUrl || FALLBACK_TICKET_URL}
          target="_blank"
          rel="noopener"
          data-testid="events-buy-tickets"
          className="group inline-flex items-center gap-2 text-slate-900 text-sm uppercase tracking-[0.2em] border-b border-slate-900 pb-1 w-fit hover:text-[#00B4D8] hover:border-[#00B4D8] transition-colors duration-300"
        >
          {t.onePass}
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 md:pb-16">
        {events.map((ev, i) => (
          <Card key={ev.slug} ev={ev} i={i} lang={lang} view={t.view} />
        ))}
      </div>
      <FadeUp className="mt-14 md:mt-2">
        <p data-testid="events-more-teaser" className="font-serif italic text-xl md:text-2xl text-slate-400 max-w-2xl">{t.more}</p>
      </FadeUp>
    </section>
  );
};
